import { useState, useRef, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, ArrowRight, Calendar, Target, TrendingUp, Users, CheckCircle2, Download, FileText, Lock, Globe, ChevronRight, Zap, BarChart3, MessageSquare, Rocket, Mail, CreditCard, Heart, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

const formSchema = z.object({
  firstName: z.string().min(2, "Please enter your first name"),
  email: z.string().email("Please enter a valid work email"),
});

type FormValues = z.infer<typeof formSchema>;

interface PersonalizedPlan {
  businessName: string;
  stage: "New" | "Established" | "Enterprise";
  signals: string[];
  steps: { title: string; description: string }[];
  outcomes: { metric: string; value: string }[];
  advancedSuggestions: { title: string; description: string; impact: string; timeToValue: string }[];
}

interface PersonalizedGrowthPlanProps {
  source?: string;
}

const INDUSTRIES = [
  { value: "", label: "Select your industry" },
  // Enterprise B2B (Primary targets - £1.5k+ retainers)
  { value: "SaaS / Technology", label: "SaaS / Technology" },
  { value: "FinTech", label: "FinTech" },
  { value: "Professional Services", label: "Professional Services" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Healthcare / MedTech", label: "Healthcare / MedTech" },
  { value: "Real Estate / PropTech", label: "Real Estate / PropTech" },
  { value: "Legal Services", label: "Legal Services" },
  { value: "Financial Services", label: "Financial Services" },
  { value: "Energy / Utilities", label: "Energy / Utilities" },
  { value: "Marketing Agency", label: "Marketing Agency" },
  { value: "Consulting", label: "Consulting" },
  // Secondary (still valid but lower priority)
  { value: "Construction", label: "Construction" },
  { value: "Trades / Home Services", label: "Trades / Home Services" },
  { value: "Other", label: "Other" },
];

const INDUSTRY_BENEFITS: Record<string, string> = {
  "SaaS / Technology": "Multi-touch ABM campaigns, LinkedIn SDR automation, and intent-based outbound to accelerate pipeline velocity",
  "FinTech": "Compliance-aware demand generation combining content syndication, paid search, and executive LinkedIn outreach",
  "Professional Services": "Authority positioning with thought leadership content funnels and high-touch account-based marketing",
  "Manufacturing": "Trade show follow-up automation, specification document funnels, and distributor channel development",
  "Healthcare / MedTech": "HIPAA-compliant lead nurturing, physician outreach sequences, and conference pipeline acceleration",
  "Real Estate / PropTech": "Investor lead scoring systems, property alert automation, and B2B landlord acquisition campaigns",
  "Legal Services": "Referral network automation, practice area content funnels, and high-value case acquisition systems",
  "Financial Services": "Compliance-approved prospecting, retirement planning funnels, and HNW client acquisition sequences",
  "Energy / Utilities": "Multi-channel B2B lead generation combining comparison platforms, direct outreach & contract acquisition",
  "Marketing Agency": "Inbound content systems, case study funnels, and strategic partnership development programs",
  "Consulting": "Executive LinkedIn positioning, speaking engagement pipelines, and enterprise RFP response systems",
  "Construction": "B2B subcontractor outreach, project pipeline management, and general contractor relationship automation",
  "Trades / Home Services": "Local SEO domination, review generation systems, and recurring service contract upselling",
};

const PRIORITY_OPTIONS = [
  { value: "pipeline", label: "Accelerate pipeline generation" },
  { value: "abm", label: "Account-based marketing programs" },
  { value: "outbound", label: "Scale outbound SDR operations" },
  { value: "cac", label: "Reduce customer acquisition cost" },
  { value: "channels", label: "Open new demand channels" },
  { value: "expansion", label: "Customer expansion & retention" },
];

const REVENUE_RANGES = [
  { value: "250k-500k", label: "£250k - £500k" },
  { value: "500k-1m", label: "£500k - £1M" },
  { value: "1m-3m", label: "£1M - £3M" },
  { value: "3m-10m", label: "£3M - £10M" },
  { value: "10m-50m", label: "£10M - £50M" },
  { value: "50m+", label: "£50M+" },
];

const BUDGET_RANGES = [
  { value: "under-1.5k", label: "Under £1,500/mo", disqualified: true },
  { value: "1.5k-3k", label: "£1,500 - £3,000/mo", disqualified: false },
  { value: "3k-5k", label: "£3,000 - £5,000/mo", disqualified: false },
  { value: "5k-10k", label: "£5,000 - £10,000/mo", disqualified: false },
  { value: "10k+", label: "£10,000+/mo", disqualified: false },
  { value: "unsure", label: "Not sure yet", disqualified: false },
];

// Decision-maker job titles (case-insensitive matching)
const DECISION_MAKER_TITLES = [
  "ceo", "coo", "cfo", "cmo", "cto", "cro", "cso",
  "chief", "founder", "co-founder", "cofounder", "owner",
  "president", "partner", "principal", "director",
  "vp", "vice president", "head of", "managing",
  "general manager", "gm", "md", "managing director",
  "decision maker", "board", "executive"
];

const FAQ_ITEMS = [
  {
    id: "google-business",
    title: "Claim/Optimize Your Google Business Profile",
    whyMatters: "Your GBP drives local discovery and trust. It's often the first impression — and determines whether they call you or a competitor.",
    howToDo: [
      "Claim and verify at business.google.com",
      "Request 5 reviews from your best customers this week"
    ],
    doesntApply: "B2B? Prioritize G2/Capterra reviews instead.",
    checklist: ["Claimed listing", "5+ reviews requested"]
  },
  {
    id: "tracking",
    title: "Baseline Tracking & Conversion Events",
    whyMatters: "You can't improve what you don't measure. Proper tracking = knowing exactly what's working.",
    howToDo: [
      "Install GA4 with conversion events on key actions",
      "Track time-to-first-response (this alone can 2x close rate)"
    ],
    doesntApply: "Using HubSpot/Salesforce? Their native dashboards work.",
    checklist: ["GA4 installed", "Conversions tracked"]
  },
  {
    id: "message-market",
    title: "Message-Market Fit",
    whyMatters: "If visitors don't instantly understand what you do and why they should care, they leave.",
    howToDo: [
      "Clarify the EXACT problem you solve in one sentence",
      "Gather proof: 3+ case studies or testimonials"
    ],
    doesntApply: "New business? Start with one clear offer first.",
    checklist: ["Problem clarity", "Proof assets ready"]
  },
  {
    id: "speed-to-lead",
    title: "Speed-to-Lead Protocol",
    whyMatters: "Leads contacted within 5 minutes are 21x more likely to convert. Every minute costs you money.",
    howToDo: [
      "Set 5-minute response SLA with mobile notifications",
      "Create templates for instant replies — that's how we can help"
    ],
    doesntApply: "High-volume? Smart automation with batch processing works.",
    checklist: ["5-min SLA set", "Templates ready"]
  },
  {
    id: "website-conversion",
    title: "Website Conversion Basics",
    whyMatters: "Your website is your 24/7 salesperson. Most sites leak leads by making action too hard.",
    howToDo: [
      "Clear CTA above the fold (visible without scrolling)",
      "Social proof in first scroll (logos, reviews, results)"
    ],
    doesntApply: "Complex B2B? Qualification forms may help quality.",
    checklist: ["Above-fold CTA", "Social proof visible"]
  }
];

const PersonalizedGrowthPlan = ({ source = "a" }: PersonalizedGrowthPlanProps) => {
  
  const [stage, setStage] = useState<"gate" | "qualifying" | "loading" | "personalized" | "disqualified">("gate");
  const [loadingStep, setLoadingStep] = useState(0);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [otherIndustry, setOtherIndustry] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [idealCustomer, setIdealCustomer] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [priorities2026, setPriorities2026] = useState<string[]>([]);
  const [otherPriority, setOtherPriority] = useState("");
  const [currentRevenue, setCurrentRevenue] = useState("");
  const [targetRevenue, setTargetRevenue] = useState("");
  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [plan, setPlan] = useState<PersonalizedPlan | null>(null);
  const [qualifyingError, setQualifyingError] = useState("");
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [checkedItems, setCheckedItems] = useState<Record<string, string[]>>({});
  const [consentScan, setConsentScan] = useState(true);
  const [isCheckingUnlock, setIsCheckingUnlock] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  // Check unlock status function
  const checkUnlockStatus = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck) return false;
    
    try {
      setIsCheckingUnlock(true);
      const { data, error } = await supabase.functions.invoke("check-unlock", {
        body: { email: emailToCheck },
      });
      
      if (error) {
        console.error("Error checking unlock status:", error);
        return false;
      }
      
      if (data?.unlocked) {
        setIsUnlocked(true);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Check unlock error:", err);
      return false;
    } finally {
      setIsCheckingUnlock(false);
    }
  }, []);

  // Check unlock status when stage changes to personalized
  useEffect(() => {
    if (stage === "personalized" && email) {
      checkUnlockStatus(email);
    }
  }, [stage, email, checkUnlockStatus]);

  // Poll for unlock status after payment redirect
  const startUnlockPolling = useCallback(async (emailToCheck: string) => {
    toast.info("Checking payment status...");
    
    // Poll every 3 seconds for up to 2 minutes
    let attempts = 0;
    const maxAttempts = 40;
    
    const poll = async () => {
      attempts++;
      const unlocked = await checkUnlockStatus(emailToCheck);
      
      if (unlocked) {
        toast.success("Payment confirmed! Report unlocked.");
        return;
      }
      
      if (attempts < maxAttempts) {
        setTimeout(poll, 3000);
      } else {
        toast.info("Payment verification pending. Refresh the page in a few minutes.");
      }
    };
    
    // Start polling after a short delay to allow redirect
    setTimeout(poll, 5000);
  }, [checkUnlockStatus]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      email: "",
    },
  });

  const extractBusinessName = (email: string): string => {
    const domain = email.split("@")[1];
    if (!domain) return "your business";
    const name = domain.split(".")[0];
    if (["gmail", "yahoo", "hotmail", "outlook", "icloud", "aol"].includes(name.toLowerCase())) {
      return "your business";
    }
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const onSubmit = async (data: FormValues) => {
    setFirstName(data.firstName);
    setEmail(data.email);
    setUnlockEmail(data.email);
    const extractedBusiness = extractBusinessName(data.email);
    setBusinessName(extractedBusiness);
    setStage("qualifying");
  };

  const handlePriorityToggle = (value: string) => {
    setPriorities2026(prev => {
      if (prev.includes(value)) {
        return prev.filter(p => p !== value);
      }
      if (prev.length >= 2) {
        return [...prev.slice(1), value];
      }
      return [...prev, value];
    });
  };

  const handleChecklistToggle = (faqId: string, item: string) => {
    setCheckedItems(prev => {
      const current = prev[faqId] || [];
      if (current.includes(item)) {
        return { ...prev, [faqId]: current.filter(i => i !== item) };
      }
      return { ...prev, [faqId]: [...current, item] };
    });
  };

  const handleQualifyingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const finalIndustry = industry === "Other" ? otherIndustry : industry;
    const finalCompanyName = companyName || businessName;
    
    if (!finalIndustry || finalIndustry.length < 2) {
      setQualifyingError("Please select or enter your industry");
      return;
    }
    if (!idealCustomer || idealCustomer.length < 5) {
      setQualifyingError("Please describe your ideal customer");
      return;
    }
    
    // Check if decision-maker based on job title
    const isDecisionMaker = jobTitle 
      ? DECISION_MAKER_TITLES.some(title => 
          jobTitle.toLowerCase().includes(title.toLowerCase())
        )
      : false;
    
    // Check if disqualified by budget
    if (monthlyBudget === "under-1.5k") {
      setQualifyingError("");
      
      // Store disqualified lead in database
      try {
        await supabase.from("form_submissions").insert({
          name: firstName,
          email,
          company_name: finalCompanyName,
          form_type: "disqualified_lead",
          additional_data: {
            industry: finalIndustry,
            monthlyBudget,
            currentRevenue,
            idealCustomer,
            source,
            disqualified: true,
            reason: "Budget under £1,500/mo",
            websiteUrl,
            priorities2026,
            jobTitle,
            isDecisionMaker,
          },
        });
      } catch (dbErr) {
        console.error("Error storing disqualified lead:", dbErr);
      }

      // Send to edge function for email sequence
      try {
        await supabase.functions.invoke("notify-form-submission", {
          body: {
            type: "disqualified",
            name: firstName,
            email,
            businessName: finalCompanyName,
            industry: finalIndustry,
            monthlyBudget,
            currentRevenue,
            source,
            jobTitle,
          },
        });
      } catch (emailErr) {
        console.error("Error triggering email sequence:", emailErr);
      }

      setStage("disqualified");
      return;
    }
    
    // If not a decision-maker, send email requesting decision-maker on call
    if (jobTitle && !isDecisionMaker) {
      try {
        await supabase.functions.invoke("notify-form-submission", {
          body: {
            type: "non_decision_maker",
            name: firstName,
            email,
            businessName: finalCompanyName,
            industry: finalIndustry,
            jobTitle,
            source,
          },
        });
      } catch (emailErr) {
        console.error("Error sending decision-maker request email:", emailErr);
      }
    }
    
    setQualifyingError("");
    setStage("loading");

    // Enhanced loading steps for website scanning
    const steps = [
      { delay: 2000, step: 1 },
      { delay: 5000, step: 2 },
      { delay: 8000, step: 3 },
      { delay: 11000, step: 4 },
    ];

    for (const { delay, step } of steps) {
      await new Promise((resolve) => setTimeout(resolve, step === 1 ? delay : delay - steps[step - 2].delay));
      setLoadingStep(step);
    }

    try {
      const { data: planData, error } = await supabase.functions.invoke("generate-growth-plan", {
        body: { 
          firstName, 
          businessName, 
          email,
          industry: finalIndustry,
          websiteUrl,
          idealCustomer,
          priorities2026: priorities2026.includes("other") ? [...priorities2026.filter(p => p !== "other"), otherPriority] : priorities2026,
          currentRevenue,
          targetRevenue,
          consentScan,
          checkedItems,
          source
        },
      });

      if (error) throw error;

      setPlan({
        ...planData,
        stage: planData.stage || "New",
        signals: planData.signals || ["Based on your inputs"],
        advancedSuggestions: planData.advancedSuggestions || getDefaultAdvancedSuggestions(finalIndustry, priorities2026)
      });
      setStage("personalized");
    } catch (error) {
      console.error("Error generating plan:", error);
      setPlan({
        businessName,
        stage: "New",
        signals: ["Based on your inputs"],
        steps: [
          { title: "Foundation Audit", description: "We analyze your current lead sources and identify the gaps costing you customers." },
          { title: "Acquisition System Build", description: "Deploy a multi-channel system that generates enquiries on autopilot." },
          { title: "Pipeline Optimization", description: "Streamline your follow-up process so no lead falls through the cracks." },
          { title: "Scale & Compound", description: "Double down on what works and build predictable growth." },
        ],
        outcomes: [
          { metric: "New Qualified Leads", value: "30-50+" },
          { metric: "Pipeline Visibility", value: "100%" },
          { metric: "Booking Rate Increase", value: "40%" },
          { metric: "Time to First Result", value: "14 days" },
        ],
        advancedSuggestions: getDefaultAdvancedSuggestions(industry, priorities2026)
      });
      setStage("personalized");
    }
  };

  const getDefaultAdvancedSuggestions = (ind: string, priorities: string[]) => {
    const suggestions = [
      { title: "Offer Architecture Overhaul", description: "Build a 3-tier value ladder with Core, Fast Track, and Concierge options. Layer guarantees to remove risk.", impact: "+25-40% average order value", timeToValue: "2-3 weeks" },
      { title: "Proof Acceleration Sprint", description: "Generate 2 detailed case studies in 30 days using our before/after framework. Video testimonials included.", impact: "+35% conversion rate", timeToValue: "30 days" },
      { title: "Conversion Research Deep-Dive", description: "Voice-of-customer interviews, on-site polls, and win/loss analysis to uncover hidden objections.", impact: "Foundation for all copy", timeToValue: "2 weeks" },
      { title: "Homepage Rewrite + A/B Testing", description: "Complete above-the-fold optimization with structured testing plan. Focus on headline, social proof, and CTA.", impact: "+50-100% lead capture", timeToValue: "3-4 weeks" },
      { title: "Lifecycle Email Engine", description: "3 automated sequences: lead magnet nurture, demo no-show recovery, and stale opportunity reactivation.", impact: "+20% lead recovery", timeToValue: "2 weeks" }
    ];
    
    if (priorities.includes("outbound")) {
      suggestions[0] = { title: "ICP List Build + Multi-Touch Sequencing", description: "Build targeted prospect lists and deploy 5-touch sequences across email, LinkedIn, and calls with objection handling.", impact: "+40-60 qualified meetings/month", timeToValue: "3 weeks" };
      suggestions[1] = { title: "Reply-to-Lead Automation", description: "Auto-route replies, hold calendar slots, send SMS confirmations. Never miss a hot lead again.", impact: "5-min response time", timeToValue: "1 week" };
    }
    
    if (priorities.includes("automation")) {
      suggestions[0] = { title: "End-to-End Server-Side Tracking", description: "Implement conversion tracking tied to CRM stages. See exactly which campaigns generate revenue, not just leads.", impact: "True ROI visibility", timeToValue: "2 weeks" };
      suggestions[1] = { title: "Lifecycle Trigger System", description: "Automated workflows from trial to PQL to SQL to expansion. The right message at the right time, every time.", impact: "+30% conversion velocity", timeToValue: "3 weeks" };
    }
    
    return suggestions;
  };

  const handleUnlock = async (method: "email" | "booking") => {
    if (method === "email") {
      if (!unlockEmail || !unlockEmail.includes("@")) {
        toast.error("Please enter a valid email address");
        return;
      }
      
      try {
        await supabase.functions.invoke("generate-growth-plan", {
          body: { 
            firstName, 
            businessName, 
            email: unlockEmail,
            unlockRequest: true,
            source 
          },
        });
        setIsUnlocked(true);
        toast.success("Full report unlocked! Check your email for the PDF.");
      } catch (error) {
        console.error("Error unlocking:", error);
        setIsUnlocked(true);
        toast.success("Report unlocked!");
      }
    } else {
      window.open("https://calendly.com/vincentgrowthpartners/client-call", "_blank");
      setIsUnlocked(true);
      toast.success("Report unlocked! Complete your booking to receive the full PDF.");
    }
  };

  const handleDownloadReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = 20;
    
    const teal = [5, 73, 82] as [number, number, number];
    const gold = [184, 140, 60] as [number, number, number];
    
    // Header
    doc.setFillColor(...teal);
    doc.rect(0, 0, pageWidth, 55, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("GROWTH PLAN", margin, 25);
    
    doc.setFontSize(14);
    doc.setTextColor(...gold);
    doc.text(`${businessName}`, margin, 38);
    
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    const revenueText = currentRevenue && targetRevenue ? `Trajectory: ${currentRevenue} to ${targetRevenue} by mid-2026` : "";
    if (revenueText) doc.text(revenueText, margin, 48);
    
    y = 70;
    
    // Stage badge
    doc.setTextColor(...teal);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`Stage: ${plan?.stage || "New"}`, margin, y);
    
    y += 15;
    
    // Quick Wins Section
    doc.setTextColor(...teal);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("QUICK WINS", margin, y);
    doc.setDrawColor(...gold);
    doc.setLineWidth(2);
    doc.line(margin, y + 3, margin + 35, y + 3);
    
    y += 12;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text("1. Foundation — We analyze your current lead sources and identify gaps costing you customers.", margin, y);
    y += 6;
    doc.text("2. Speed-to-Lead — Get your response time under 5 mins. This alone can 2x your close rate.", margin, y);
    
    y += 20;
    
    // Section: Your Game Plan
    doc.setTextColor(...teal);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("YOUR 90-DAY GAME PLAN", margin, y);
    doc.setDrawColor(...gold);
    doc.setLineWidth(2);
    doc.line(margin, y + 3, margin + 70, y + 3);
    
    y += 15;
    
    doc.setFontSize(11);
    plan?.steps.forEach((step, index) => {
      if (y > 250) {
        doc.addPage();
        y = 20;
      }
      
      doc.setTextColor(...teal);
      doc.setFont("helvetica", "bold");
      doc.text(`${index + 1}. ${step.title}`, margin, y);
      y += 7;
      
      doc.setTextColor(60, 60, 60);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(step.description, contentWidth - 10);
      doc.text(lines, margin + 5, y);
      y += lines.length * 5 + 8;
    });
    
    // Advanced suggestions (if unlocked)
    if (isUnlocked && plan?.advancedSuggestions) {
      if (y > 200) {
        doc.addPage();
        y = 20;
      }
      
      y += 10;
      doc.setTextColor(...teal);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("HIGH-LEVERAGE GROWTH PLAYS", margin, y);
      doc.setDrawColor(...gold);
      doc.line(margin, y + 3, margin + 80, y + 3);
      y += 15;
      
      doc.setFontSize(11);
      plan.advancedSuggestions.forEach((sug, index) => {
        if (y > 250) {
          doc.addPage();
          y = 20;
        }
        
        doc.setTextColor(...teal);
        doc.setFont("helvetica", "bold");
        doc.text(`${index + 1}. ${sug.title}`, margin, y);
        y += 7;
        
        doc.setTextColor(60, 60, 60);
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(sug.description, contentWidth - 10);
        doc.text(lines, margin + 5, y);
        y += lines.length * 5 + 3;
        
        doc.setTextColor(...gold);
        doc.setFont("helvetica", "italic");
        doc.text(`Impact: ${sug.impact} | Time to value: ${sug.timeToValue}`, margin + 5, y);
        y += 12;
      });
    }
    
    // CTA page
    doc.addPage();
    y = 40;
    
    doc.setFillColor(245, 245, 240);
    doc.rect(margin, y, contentWidth, 60, 'F');
    doc.setDrawColor(...gold);
    doc.setLineWidth(1);
    doc.rect(margin, y, contentWidth, 60, 'S');
    
    doc.setTextColor(...teal);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("READY TO IMPLEMENT?", margin + 10, y + 18);
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text("Book a free strategy call and we'll build this with you.", margin + 10, y + 32);
    doc.text("https://vincentgrowthpartners.co.uk/book-call?utm_source=pdf&utm_medium=growth-plan", margin + 10, y + 45);
    
    // Footer
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(9);
    doc.text(`(c) ${new Date().getFullYear()} Vincent Growth Partners`, margin, 280);
    
    doc.save(`${businessName.replace(/\s+/g, '-')}-Growth-Plan.pdf`);
    toast.success("PDF downloaded!");
  };

  // Gate Screen
  if (stage === "gate") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#054952] via-[#065a64] to-[#0a7a87] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <Sparkles className="w-8 h-8 text-[#F5D77A]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              What would predictable pipeline look like for your business?
            </h1>
            <p className="text-white/90 text-lg">
              Get a strategic growth assessment. We'll analyze your market position and deliver 5 prioritized pipeline acceleration plays.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          placeholder="First name"
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-300" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Work email"
                          className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage className="text-red-300" />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full h-12 bg-[#F5D77A] hover:bg-[#f0cf60] text-[#054952] font-semibold text-lg"
                >
                  Get Strategic Assessment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
            </Form>
            <p className="text-white/70 text-sm text-center mt-4">
              For companies serious about revenue growth. No sales fluff.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Qualifying Questions Screen
  if (stage === "qualifying") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#054952] via-[#065a64] to-[#0a7a87] flex items-center justify-center p-4">
        <div className="w-full max-w-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <Target className="w-8 h-8 text-[#F5D77A]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Let's scope your pipeline, {firstName}
            </h1>
            <p className="text-white/90">
              So we can build your strategic growth roadmap.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl">
            <form onSubmit={handleQualifyingSubmit} className="space-y-6">
              {/* Company & Role */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/90 text-sm mb-2 font-medium">Company name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Acme Inc"
                    className="w-full h-12 px-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-white/90 text-sm mb-2 font-medium">Your job title</label>
                  <input
                    type="text"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="e.g. CEO, VP Marketing"
                    className="w-full h-12 px-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Ideal Customer */}
              <div>
                <label className="block text-white/90 text-sm mb-2 font-medium">Describe your ideal customer profile (ICP)</label>
                <textarea
                  value={idealCustomer}
                  onChange={(e) => setIdealCustomer(e.target.value)}
                  placeholder="e.g. Series A-C SaaS companies with 50-500 employees seeking to scale revenue operations"
                  className="w-full min-h-[80px] px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors resize-none"
                />
              </div>

              {/* Industry Dropdown */}
              <div>
                <label className="block text-white/90 text-sm mb-2 font-medium">Industry</label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg bg-white/10 border border-white/20 text-white focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px', appearance: 'none' }}
                >
                  {INDUSTRIES.map((ind) => (
                    <option key={ind.value} value={ind.value} className="bg-[#054952] text-white">
                      {ind.label}
                    </option>
                  ))}
                </select>
                {industry === "Other" && (
                  <input
                    type="text"
                    value={otherIndustry}
                    onChange={(e) => setOtherIndustry(e.target.value)}
                    placeholder="Enter your industry"
                    className="w-full h-12 px-4 mt-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors"
                  />
                )}
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-white/90 text-sm mb-2 font-medium">Website URL (optional)</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourbusiness.com"
                  className="w-full h-12 px-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors"
                />
                <div className="flex items-center gap-2 mt-2">
                  <Checkbox 
                    id="consent-scan" 
                    checked={consentScan}
                    onCheckedChange={(checked) => setConsentScan(checked as boolean)}
                    className="border-white/30 data-[state=checked]:bg-[#F5D77A] data-[state=checked]:border-[#F5D77A]"
                  />
                  <label htmlFor="consent-scan" className="text-white/70 text-xs cursor-pointer">
                    Allow basic scan of public pages to personalize my plan
                  </label>
                </div>
                <p className="text-[#F5D77A]/80 text-xs mt-2 flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  Including your website significantly improves AI report accuracy and actionable insights
                </p>
              </div>

              {/* 2026 Priorities */}
              <div>
                <label className="block text-white/90 text-sm mb-2 font-medium">Primary growth objectives for 2025-26? (Pick up to 2)</label>
                <div className="grid grid-cols-2 gap-2">
                  {PRIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handlePriorityToggle(opt.value)}
                      className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${
                        priorities2026.includes(opt.value)
                          ? "bg-[#F5D77A] text-[#054952] font-medium"
                          : "bg-white/10 text-white/80 hover:bg-white/20"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => handlePriorityToggle("other")}
                    className={`px-3 py-2 rounded-lg text-sm text-left transition-all ${
                      priorities2026.includes("other")
                        ? "bg-[#F5D77A] text-[#054952] font-medium"
                        : "bg-white/10 text-white/80 hover:bg-white/20"
                    }`}
                  >
                    Other
                  </button>
                </div>
                {priorities2026.includes("other") && (
                  <input
                    type="text"
                    value={otherPriority}
                    onChange={(e) => setOtherPriority(e.target.value)}
                    placeholder="What's your priority?"
                    className="w-full h-10 px-4 mt-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors text-sm"
                  />
                )}
              </div>

              {/* Revenue Goals */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-white/90 text-sm mb-2 font-medium">Current annual revenue</label>
                  <select
                    value={currentRevenue}
                    onChange={(e) => setCurrentRevenue(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors cursor-pointer text-sm"
                    style={{ appearance: 'none' }}
                  >
                    <option value="" className="bg-[#054952]">Select</option>
                    {REVENUE_RANGES.map((r) => (
                      <option key={r.value} value={r.label} className="bg-[#054952]">{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white/90 text-sm mb-2 font-medium">Target by mid-2026</label>
                  <select
                    value={targetRevenue}
                    onChange={(e) => setTargetRevenue(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors cursor-pointer text-sm"
                    style={{ appearance: 'none' }}
                  >
                    <option value="" className="bg-[#054952]">Select</option>
                    {REVENUE_RANGES.map((r) => (
                      <option key={r.value} value={r.label} className="bg-[#054952]">{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Monthly Marketing Budget */}
              <div>
                <label className="block text-white/90 text-sm mb-2 font-medium">Monthly marketing investment budget</label>
                <select
                  value={monthlyBudget}
                  onChange={(e) => setMonthlyBudget(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white focus:bg-white/20 focus:border-[#F5D77A] focus:outline-none transition-colors cursor-pointer text-sm"
                  style={{ appearance: 'none' }}
                >
                  <option value="" className="bg-[#054952]">Select budget range</option>
                  {BUDGET_RANGES.map((r) => (
                    <option key={r.value} value={r.value} className="bg-[#054952]">{r.label}</option>
                  ))}
                </select>
                <p className="text-white/50 text-xs mt-1">Helps us recommend the right package</p>
              </div>

              {qualifyingError && (
                <p className="text-red-300 text-sm">{qualifyingError}</p>
              )}

              <Button
                type="submit"
                className="w-full h-12 bg-[#F5D77A] hover:bg-[#f0cf60] text-[#054952] font-semibold text-lg"
              >
                Generate Strategic Roadmap
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Disqualified Screen - Still offer £9 report + soft rejection
  if (stage === "disqualified") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#054952] via-[#065a64] to-[#0a7a87] flex items-center justify-center p-4">
        <div className="w-full max-w-lg animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <Heart className="w-8 h-8 text-[#F5D77A]" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Thanks for your interest, {firstName}!
            </h1>
            <p className="text-white/90 text-lg leading-relaxed">
              Based on your current budget, our enterprise retainers might not be the right fit just yet — but we can still deliver value.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl space-y-6">
            {/* £9 Report Upsell - Primary CTA */}
            <div className="bg-[#F5D77A]/20 rounded-xl p-6 border-2 border-[#F5D77A]/40 space-y-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F5D77A]" />
                Get Your AI Growth Report
              </h2>
              <p className="text-white/90 text-sm">
                Even without our enterprise service, get a comprehensive AI-generated growth report with actionable insights tailored to your industry and business.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#F5D77A]" />
                  <span>5-page strategic growth assessment</span>
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#F5D77A]" />
                  <span>Industry-specific opportunity analysis</span>
                </div>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-[#F5D77A]" />
                  <span>Actionable next steps you can implement today</span>
                </div>
              </div>
              <a
                href={`https://buy.stripe.com/cN28Ao3gI6Jn2Fa6op?prefilled_email=${encodeURIComponent(email)}&client_reference_id=${encodeURIComponent(email)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-[#F5D77A] hover:bg-[#f0cf60] text-[#054952] font-bold py-4 px-4 rounded-lg transition-colors text-lg"
              >
                <CreditCard className="w-5 h-5" />
                Unlock Full Report — £9
              </a>
              <p className="text-white/60 text-xs text-center">One-time payment • Instant PDF delivery</p>
            </div>

            {/* Free Resources */}
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F5D77A]" />
                Free Resources
              </h2>
              <Link
                to="/white-papers"
                className="flex items-center justify-between w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-4 rounded-lg border border-white/20 transition-colors"
              >
                <span>Access Free White Papers & Guides</span>
                <ExternalLink className="w-5 h-5" />
              </Link>
            </div>

            {/* Check Back */}
            <div className="border-t border-white/20 pt-6 space-y-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#F5D77A]" />
                Let's Reconnect
              </h2>
              <p className="text-white/80 text-sm">
                We'll reach out in 60 days to see how you're progressing. Growth happens fast — you might be ready for our enterprise services sooner than you think.
              </p>
            </div>

            {/* Direct Contact */}
            <div className="border-t border-white/20 pt-6">
              <a
                href="https://wa.me/447450620757"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-4 rounded-lg border border-white/20 transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                WhatsApp: 07450 620757
              </a>
            </div>

            {/* Encouragement */}
            <div className="bg-[#F5D77A]/10 rounded-lg p-4 border border-[#F5D77A]/20">
              <p className="text-white/90 text-sm text-center">
                <strong className="text-[#F5D77A]">We're rooting for you!</strong> Keep building, keep growing — and when you're ready for enterprise-grade demand generation, we'll be here.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading Screen with enhanced progress
  if (stage === "loading") {
    // Only show crawling step if website URL was provided
    const loadingSteps = websiteUrl && consentScan
      ? [
          { text: "Crawling your website...", icon: Globe },
          { text: "Understanding your offers & proof assets...", icon: FileText },
          { text: "Mapping your funnel & tech stack...", icon: BarChart3 },
          { text: "Personalizing your plan...", icon: Sparkles },
        ]
      : [
          { text: "Analyzing your industry...", icon: Target },
          { text: "Understanding your goals & priorities...", icon: FileText },
          { text: "Mapping growth opportunities...", icon: BarChart3 },
          { text: "Personalizing your plan...", icon: Sparkles },
        ];

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#054952] via-[#065a64] to-[#0a7a87] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="relative w-24 h-24 mx-auto mb-8">
            <div className="absolute inset-0 border-4 border-[#F5D77A]/30 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-[#F5D77A] rounded-full animate-spin"></div>
            <Sparkles className="absolute inset-0 m-auto w-10 h-10 text-[#F5D77A]" />
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-white/20 rounded-full h-2 mb-8">
            <div 
              className="bg-[#F5D77A] h-2 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${((loadingStep + 1) / loadingSteps.length) * 100}%` }}
            />
          </div>
          
          <div className="space-y-4">
            {loadingSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div
                  key={index}
                  className={`flex items-center gap-4 transition-all duration-500 ${
                    index <= loadingStep ? "opacity-100" : "opacity-30"
                  }`}
                >
                  {index < loadingStep ? (
                    <CheckCircle2 className="w-6 h-6 text-[#F5D77A] flex-shrink-0" />
                  ) : index === loadingStep ? (
                    <div className="w-6 h-6 border-2 border-[#F5D77A] border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  ) : (
                    <Icon className="w-6 h-6 text-white/50 flex-shrink-0" />
                  )}
                  <span className="text-white text-lg text-left">{step.text}</span>
                </div>
              );
            })}
          </div>
          
          <p className="text-white/60 text-sm mt-8">
            This takes about 15-20 seconds
          </p>
        </div>
      </div>
    );
  }

  // Personalized Page
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#054952] via-[#065a64] to-[#0a7a87] py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-[#F5D77A] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Your personalised growth plan
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Hey {firstName},<br />
            <span className="text-[#F5D77A]">here's what {businessName} could look like</span> if lead generation was inevitable.
          </h1>
          
          {/* Industry-specific benefit message */}
          {industry && industry !== "Other" && INDUSTRY_BENEFITS[industry] && (
            <p className="text-lg text-[#F5D77A] bg-white/10 rounded-lg px-6 py-3 mb-4 inline-block">
              We found your industry benefits best from: <span className="font-semibold">{INDUSTRY_BENEFITS[industry]}</span>
            </p>
          )}
          
          {/* Harder CTA for "Other" industry */}
          {(industry === "Other" || !INDUSTRY_BENEFITS[industry]) && (
            <div className="bg-[#F5D77A]/20 border border-[#F5D77A]/50 rounded-lg px-6 py-4 mb-4 max-w-2xl mx-auto">
              <p className="text-white font-medium mb-2">Your industry is unique — let's talk strategy.</p>
              <p className="text-white/80 text-sm mb-3">We'd love to understand your specific challenges and build a custom growth plan together.</p>
              <Button
                onClick={() => window.open("https://calendly.com/vincentgrowthpartners/client-call", "_blank")}
                className="bg-[#F5D77A] hover:bg-[#f0cf60] text-[#054952] font-semibold"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Book Your Strategy Call
              </Button>
            </div>
          )}
          
          <p className="text-xl text-white/90 max-w-2xl mx-auto mb-4">
            Predictable enquiries. A cleaner pipeline. A system that compounds — not guesswork.
          </p>
          {currentRevenue && targetRevenue && (
            <p className="text-lg text-[#F5D77A] font-medium">
              Trajectory to mid-2026: {currentRevenue} → {targetRevenue}
            </p>
          )}
        </div>
      </section>

      {/* Growth Starter Report - FAQ Accordion */}
      <section className="py-16 px-4 bg-[#F5F5F0]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#054952] to-[#0a7a87] p-6 text-white">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-6 h-6 text-[#F5D77A]" />
                <h2 className="text-2xl font-bold">Your Growth Starter Report</h2>
              </div>
              <p className="text-white/90 text-sm">
                These are foundational best practices. Some may not apply to your business — but ensuring these are covered eliminates the most common blockers to consistent lead flow.
              </p>
            </div>
            
            <div className="p-6">
              <Accordion type="multiple" className="space-y-3">
                {FAQ_ITEMS.map((faq) => (
                  <AccordionItem key={faq.id} value={faq.id} className="border border-border/50 rounded-xl px-4 overflow-hidden">
                    <AccordionTrigger className="text-left hover:no-underline py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#054952]/10 flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-[#054952]" />
                        </div>
                        <span className="font-semibold text-foreground">{faq.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <div className="space-y-4 pl-11">
                        <div>
                          <h4 className="font-medium text-[#054952] text-sm mb-1">Why it matters</h4>
                          <p className="text-muted-foreground text-sm">{faq.whyMatters}</p>
                        </div>
                        
                        <div>
                          <h4 className="font-medium text-[#054952] text-sm mb-2">How to do it</h4>
                          <ul className="space-y-1">
                            {faq.howToDo.map((step, idx) => (
                              <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                                <ChevronRight className="w-4 h-4 text-[#F5D77A] flex-shrink-0 mt-0.5" />
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="bg-muted/50 rounded-lg p-3">
                          <h4 className="font-medium text-[#054952] text-sm mb-1">Doesn't apply?</h4>
                          <p className="text-muted-foreground text-sm">{faq.doesntApply}</p>
                        </div>
                        
                        <div className="border-t pt-4">
                          <h4 className="font-medium text-[#054952] text-sm mb-2">Quick Checklist</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {faq.checklist.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Checkbox 
                                  id={`${faq.id}-${idx}`}
                                  checked={(checkedItems[faq.id] || []).includes(item)}
                                  onCheckedChange={() => handleChecklistToggle(faq.id, item)}
                                  className="border-[#054952]/30 data-[state=checked]:bg-[#054952] data-[state=checked]:border-[#054952]"
                                />
                                <label htmlFor={`${faq.id}-${idx}`} className="text-sm text-muted-foreground cursor-pointer">
                                  {item}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              
              <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleDownloadReport}
                  className="flex-1 bg-[#054952] hover:bg-[#065a64] text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Starter Report
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Suggestions - 2 Visible + 3 Blurred */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-foreground mb-3">
              High-Leverage Growth Plays for {businessName}
            </h2>
            <p className="text-muted-foreground">
              Tailored suggestions based on your stage ({plan?.stage || "New"}) and priorities.
            </p>
          </div>
          
          <div className="space-y-4">
            {/* Visible Cards (first 2) */}
            {plan?.advancedSuggestions.slice(0, 2).map((suggestion, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-border/50 p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#054952] to-[#0a7a87] flex items-center justify-center flex-shrink-0">
                    {index === 0 ? <Rocket className="w-6 h-6 text-[#F5D77A]" /> : <Zap className="w-6 h-6 text-[#F5D77A]" />}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground mb-2">{suggestion.title}</h3>
                    <p className="text-muted-foreground mb-4">{suggestion.description}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-1 text-sm bg-[#F5D77A]/20 text-[#054952] px-3 py-1 rounded-full font-medium">
                        <TrendingUp className="w-4 h-4" />
                        {suggestion.impact}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm bg-[#054952]/10 text-[#054952] px-3 py-1 rounded-full">
                        <Calendar className="w-4 h-4" />
                        {suggestion.timeToValue}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Blurred Cards (last 3) */}
            {!isUnlocked && (
              <div className="relative">
                <div className="space-y-4 opacity-50">
                  {plan?.advancedSuggestions.slice(2, 5).map((suggestion, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl border border-border/50 p-6 shadow-sm"
                      style={{ filter: 'blur(6px)' }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <Lock className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-foreground mb-2">{suggestion.title}</h3>
                          <p className="text-muted-foreground mb-4">{suggestion.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Unlock Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-background via-background/80 to-transparent rounded-xl">
                  <div className="text-center p-8 max-w-md">
                    <Lock className="w-12 h-12 text-[#054952] mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-2">
                      Unlock 3 more high-leverage plays
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Get the full report by email, book a call, or purchase instant access.
                    </p>
                    
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="Your email"
                          value={unlockEmail}
                          onChange={(e) => setUnlockEmail(e.target.value)}
                          className="flex-1"
                        />
                        <Button 
                          onClick={() => handleUnlock("email")}
                          className="bg-[#054952] hover:bg-[#065a64] text-white"
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Send PDF
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-border"></div>
                        <span className="text-muted-foreground text-sm">or</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                      
                      <Button 
                        onClick={() => handleUnlock("booking")}
                        className="w-full bg-[#F5D77A] hover:bg-[#f0cf60] text-[#054952] font-semibold"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Book a Call — Unlock Instantly
                      </Button>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-border"></div>
                        <span className="text-muted-foreground text-sm">or</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                      
                      <Button 
                        onClick={() => {
                          // Open Stripe checkout
                          window.open(`https://buy.stripe.com/cN28Ao3gI6Jn2Fa6op?prefilled_email=${encodeURIComponent(unlockEmail || email)}&client_reference_id=${encodeURIComponent(unlockEmail || email)}`, "_blank");
                          // Start polling for payment confirmation
                          if (unlockEmail || email) {
                            startUnlockPolling(unlockEmail || email);
                          } else {
                            toast.info("Complete payment, then refresh this page to unlock.");
                          }
                        }}
                        className="w-full bg-gradient-to-r from-[#182A1F] to-[#1f3a28] hover:from-[#1f3a28] hover:to-[#254530] text-white font-semibold"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Buy Full Report — £9
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show all cards if unlocked */}
            {isUnlocked && plan?.advancedSuggestions.slice(2, 5).map((suggestion, index) => (
              <div
                key={index + 2}
                className="bg-white rounded-xl border border-[#F5D77A] p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F5D77A] to-[#f0cf60] flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-[#054952]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold text-foreground">{suggestion.title}</h3>
                      <span className="text-xs bg-[#F5D77A]/30 text-[#054952] px-2 py-0.5 rounded-full">Unlocked</span>
                    </div>
                    <p className="text-muted-foreground mb-4">{suggestion.description}</p>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center gap-1 text-sm bg-[#F5D77A]/20 text-[#054952] px-3 py-1 rounded-full font-medium">
                        <TrendingUp className="w-4 h-4" />
                        {suggestion.impact}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm bg-[#054952]/10 text-[#054952] px-3 py-1 rounded-full">
                        <Calendar className="w-4 h-4" />
                        {suggestion.timeToValue}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Game Plan Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              The game plan we'd run for {businessName}
            </h2>
            <p className="text-lg text-muted-foreground">
              If success was inevitable, this is the exact framework we'd execute over the next 90 days:
            </p>
          </div>

          <div className="space-y-6">
            {plan?.steps.map((step, index) => (
              <div
                key={index}
                className="flex gap-6 p-6 bg-background rounded-xl border border-border/50 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-full bg-[#054952]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-[#054952]">{index + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 90-Day Outcomes Section */}
      <section className="py-20 px-4 bg-[#054952]/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              What changes over the next 90 days
            </h2>
            <p className="text-lg text-muted-foreground">
              This is what clients typically see when the system is implemented correctly:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {plan?.outcomes.map((outcome, index) => (
              <div
                key={index}
                className="p-6 bg-background rounded-xl border border-border/50 shadow-sm text-center"
              >
                <div className="w-12 h-12 rounded-full bg-[#F5D77A]/20 flex items-center justify-center mx-auto mb-4">
                  {index === 0 && <Users className="w-6 h-6 text-[#054952]" />}
                  {index === 1 && <Target className="w-6 h-6 text-[#054952]" />}
                  {index === 2 && <TrendingUp className="w-6 h-6 text-[#054952]" />}
                  {index === 3 && <Calendar className="w-6 h-6 text-[#054952]" />}
                </div>
                <p className="text-3xl font-bold text-[#054952] mb-2">{outcome.value}</p>
                <p className="text-muted-foreground">{outcome.metric}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-[#054952] via-[#065a64] to-[#0a7a87]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to make this happen?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Book a free strategy call — we'll walk through your plan and show you exactly how to execute it.
          </p>
          <Button
            size="lg"
            className="bg-[#F5D77A] hover:bg-[#f0cf60] text-[#054952] font-semibold text-lg px-8 py-6"
            onClick={() => window.open("https://calendly.com/vincentgrowthpartners/client-call", "_blank")}
          >
            Book your free call
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <p className="text-white/70 text-sm mt-4">
            No obligation. No pressure. Just clarity.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PersonalizedGrowthPlan;
